import React from 'react';
import type { MenuProps } from 'antd';
import type { ReactNode } from 'react';
import {
  DatabaseOutlined,
  ThunderboltOutlined,
  WifiOutlined,
  MedicineBoxOutlined,
  BranchesOutlined,
  AppstoreOutlined,
  HddOutlined,
  PartitionOutlined,
} from '@ant-design/icons';

type AntdMenuItem = NonNullable<Required<MenuProps>['items'][number]>;
type AntdMenuItemWithChildren = Extract<AntdMenuItem, { children?: AntdMenuItem[] | MenuProps['items'] }>;

/**
 * Получить дочерние элементы меню
 */
export function getMenuChildren(node: AntdMenuItem): AntdMenuItem[] | undefined {
  if ('children' in node) {
    const rawChildren = (node as AntdMenuItemWithChildren).children;
    if (Array.isArray(rawChildren) && rawChildren.length > 0) {
      return rawChildren as AntdMenuItem[];
    }
  }
  return undefined;
}

/**
 * Получить ключ пункта меню
 */
export function getMenuItemKey(item: AntdMenuItem | null | undefined): string | undefined {
  if (!item || typeof item !== 'object') {
    return undefined;
  }
  const key = (item as { key?: string | number }).key;
  return typeof key === 'string' ? key : undefined;
}

/**
 * Карта иконок для навигации устройств
 */
export const navigationIconMap: Record<string, ReactNode> = {
  database: React.createElement(DatabaseOutlined),
  plug: React.createElement(ThunderboltOutlined),
  wifi: React.createElement(WifiOutlined),
  medkit: React.createElement(MedicineBoxOutlined),
  shuffle: React.createElement(BranchesOutlined),
  layers: React.createElement(AppstoreOutlined),
  server: React.createElement(HddOutlined),
  branches: React.createElement(BranchesOutlined),
  partition: React.createElement(PartitionOutlined),
};

/**
 * Карта fallback для переводов навигации
 */
export const navigationLabelFallbackMap: Record<string, string> = {
  'all devices': 'devices:navigation.all',
  'switches': 'devices:navigation.switches.root',
  'managed': 'devices:navigation.switches.managed',
  'soho': 'devices:navigation.switches.soho',
  'not activated': 'devices:navigation.switches.unact',
  'without uplink': 'devices:navigation.switches.notUplink',
  'without downlink': 'devices:navigation.switches.notDnlink',
  'all switches': 'devices:navigation.switches.all',
  'olt': 'devices:navigation.switches.olt',
  'onu': 'devices:navigation.switches.onu',
  'radio equipment': 'devices:navigation.radio.root',
  'linked to customers': 'devices:navigation.radio.customers',
  'without customers': 'devices:navigation.radio.woCustomers',
  'inactive': 'devices:navigation.radio.inactive',
  'all radio devices': 'devices:navigation.radio.all',
  'media converters': 'devices:navigation.medic',
  'sensors': 'devices:navigation.sensors',
  'custom categories': 'devices:navigation.custom.root',
};

/**
 * Разрешить иконку навигации по имени
 */
export function resolveNavigationIcon(name?: string): ReactNode | undefined {
  if (!name) {
    return undefined;
  }
  return navigationIconMap[name.toLowerCase()];
}

